import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../utils/api';
import './ConfigPage.css';

function TokenBudgets() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      setError(null);
      const data = await adminAPI.getTokenBudgets();
      setConfig(data);
    } catch (err) {
      console.error('Failed to fetch token budget config:', err);
      setError(err.response?.data?.detail || 'Failed to load token budget configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleChange = (tier, field, value) => {
    setConfig(prev => ({
      ...prev,
      [tier]: {
        ...prev[tier],
        [field]: parseInt(value) || 0
      }
    }));
    setHasChanges(true);
  };

  const handleThresholdChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      query_length_thresholds: {
        ...prev.query_length_thresholds,
        [field]: parseInt(value) || 0
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await adminAPI.updateTokenBudgets(config);
      setConfig(updated);
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to save token budget config:', err);
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="config-page">
        <div className="page-header">
          <h1 className="page-title">Token Budgets</h1>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <span>Loading token budget configuration...</span>
        </div>
      </div>
    );
  }

  const tiers = [
    { key: 'minimal', label: 'Minimal', desc: 'For simple factual queries' },
    { key: 'standard', label: 'Standard', desc: 'Default for most queries' },
    { key: 'generous', label: 'Generous', desc: 'For analysis and code tasks' },
    { key: 'maximum', label: 'Maximum', desc: 'For content generation' }
  ];

  return (
    <div className="config-page">
      <div className="page-header">
        <h1 className="page-title">Token Budgets</h1>
        <p className="page-subtitle">
          Configure token limits for different query types. These settings control how many tokens are allocated to LLM responses.
        </p>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {hasChanges && (
        <div className="save-bar">
          <span>You have unsaved changes</span>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      <div className="config-grid">
        {tiers.map(tier => (
          <div key={tier.key} className="config-card">
            <div className="card-header">
              <h3>{tier.label}</h3>
              <span className="card-desc">{tier.desc}</span>
            </div>
            <div className="config-fields">
              <div className="field-group">
                <label>Stage 1 Max Tokens</label>
                <input
                  type="number"
                  value={config?.[tier.key]?.stage1_max || 0}
                  onChange={(e) => handleChange(tier.key, 'stage1_max', e.target.value)}
                />
                <span className="field-hint">Max output tokens per council model</span>
              </div>
              <div className="field-group">
                <label>Synthesis Max Tokens</label>
                <input
                  type="number"
                  value={config?.[tier.key]?.synthesis_max || 0}
                  onChange={(e) => handleChange(tier.key, 'synthesis_max', e.target.value)}
                />
                <span className="field-hint">Max tokens for final synthesis</span>
              </div>
              <div className="field-group">
                <label>Truncate To (chars)</label>
                <input
                  type="number"
                  value={config?.[tier.key]?.truncate_to || 0}
                  onChange={(e) => handleChange(tier.key, 'truncate_to', e.target.value)}
                />
                <span className="field-hint">Max chars per response before synthesis</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="config-section">
        <h2>Query Length Thresholds</h2>
        <p className="section-desc">
          These thresholds determine how queries are classified into budget tiers based on length.
        </p>
        <div className="threshold-fields">
          <div className="field-group inline">
            <label>Generous Threshold</label>
            <input
              type="number"
              value={config?.query_length_thresholds?.generous || 200}
              onChange={(e) => handleThresholdChange('generous', e.target.value)}
            />
            <span className="field-hint">Queries longer than this get generous budget</span>
          </div>
          <div className="field-group inline">
            <label>Simple Threshold</label>
            <input
              type="number"
              value={config?.query_length_thresholds?.simple || 50}
              onChange={(e) => handleThresholdChange('simple', e.target.value)}
            />
            <span className="field-hint">Queries shorter than this may get minimal budget</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TokenBudgets;
